import { useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getRequest } from '@/api/rest/hooks/requests/get';
import { usePostAuthenticationOtp, usePostAuthenticationSignIn } from '@/api/rest/resources/authentication';
import { usePostTokenSignOut } from '@/api/rest/resources/token';
import {
  PostAuthenticationOtpRequestBodyData,
  PostAuthenticationOtpSuccessDto,
  PostAuthenticationSignInRequestBodyData,
  PostAuthenticationSignInSuccessDto,
} from '@/api/rest/resources/types/authentication';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import {
  GET_USER_ME_PATH,
  GetUserMeSuccessDto,
  PostUserResetPasswordRequestBodyData,
  PostUserResetPasswordSuccessDto,
  PostUserSignUpRequestBodyData,
  PostUserSignUpSuccessDto,
  User,
} from '@/api/rest/resources/types/user';
import { usePostUserResetPassword, usePostUserSignUp } from '@/api/rest/resources/user';
import { paths } from '@/routing';

import { logInEvent, logOutEvent } from '../analytics/hooks/EventTrackers';
import { Sentry } from '../logs';
import { Logger } from '../logs/logger';
import { StorageStrategy } from '../storage/StorageStrategy';
import { TokenStorageKeys } from '.';
import { AuthenticatedUser, AuthenticationProvider, Authenticator } from './AuthenticationContext';
import { tokenRefresher } from './TokenRefresher';

const IMPERSONATED_BY_STORAGE_KEY = 'impersonatedBy';

function isPostAuthenticationOtpSuccessDto(
  value: PostAuthenticationOtpSuccessDto | PostAuthenticationSignInSuccessDto,
): value is PostAuthenticationOtpSuccessDto {
  return value && typeof (value as PostAuthenticationOtpSuccessDto).impersonated_by === 'string';
}

interface CustomLandlerAuthenticationProviderProps extends PropsWithChildren {
  tokenStorage: StorageStrategy;
}

type AuthFunctions = Omit<
  Authenticator,
  'accessToken' | 'isLoading' | 'isSignedIn' | 'isBooted' | 'authenticatedUser' | 'setUser'
>;

export const CustomLandlerAuthenticationProvider: React.FC<CustomLandlerAuthenticationProviderProps> = ({
  children,
  tokenStorage,
}) => {
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser>({ user: null });
  const [accessToken, setAccessToken] = useState<string | undefined>();
  const { mutate: signUp, isPending: isSigningUp } = usePostUserSignUp();
  const { mutate: signIn, isPending: isSigningIn } = usePostAuthenticationSignIn();
  const { mutate: signInWithToken, isPending: isSigningInWithToken } = usePostAuthenticationOtp();
  const { mutate: resetPassword, isPending: isResettingPassword } = usePostUserResetPassword();
  const { mutateAsync: tokenSignOut } = usePostTokenSignOut();
  const [isTokenLoadedFromStorage, setIsTokenLoadedFromStorage] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const setUser = useCallback((updatedUser: User) => {
    if (!updatedUser) {
      throw new Error('The user can not be null');
    }
    setAuthenticatedUser((prevAuthenticatedUser) => ({ ...prevAuthenticatedUser, user: updatedUser }));
    Sentry.setUser(updatedUser);
  }, []);

  const handleSignInSuccess = async (
    response:
      | PostUserSignUpSuccessDto
      | PostAuthenticationSignInSuccessDto
      | PostAuthenticationOtpSuccessDto
      | PostUserResetPasswordSuccessDto,
  ) => {
    const isOtpResponse = isPostAuthenticationOtpSuccessDto(response);

    const impersonatedBy = isOtpResponse ? (response.impersonated_by ?? undefined) : undefined; // make sure its undefined and not null

    const { user } = response;

    // 'other' is being used as a catch all on the BE and is not supposed to happen
    if (user.membership.type === MembershipWithOrganizationTypeEnum.other) {
      throw new Error(`Unhandled membership type ${user.membership.type}`);
    }

    tokenStorage.setItem(TokenStorageKeys.AUTH_TOKEN, response.token.access);
    tokenStorage.setItem(TokenStorageKeys.REFRESH_TOKEN, response.token.refresh);

    if (impersonatedBy) {
      sessionStorage.setItem(IMPERSONATED_BY_STORAGE_KEY, impersonatedBy);
    } else {
      sessionStorage.removeItem(IMPERSONATED_BY_STORAGE_KEY);
    }

    if (i18next.language !== user.language) {
      await i18next.changeLanguage(user.language);
    }

    setAccessToken(response.token.access);
    setAuthenticatedUser({
      impersonatedBy,
      user,
    });

    Sentry.setUser(user);
    await queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
  };

  const resetAuth = () => {
    tokenStorage.removeItem(TokenStorageKeys.AUTH_TOKEN);
    tokenStorage.removeItem(TokenStorageKeys.REFRESH_TOKEN);
    sessionStorage.removeItem(IMPERSONATED_BY_STORAGE_KEY);

    setAccessToken(undefined);

    setAuthenticatedUser({ user: null });

    Sentry.setUser(null);

    queryClient.removeQueries();
    queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
  };

  const auth = useMemo<AuthFunctions>(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    () => ({
      async signUp(data: PostUserSignUpRequestBodyData): Promise<AuthenticatedUser> {
        return new Promise((resolve, reject) => {
          signUp(
            { bodyData: data },
            {
              onSuccess: async (response) => {
                try {
                  await handleSignInSuccess(response);
                  const authenticatedUserResponse: AuthenticatedUser = {
                    user: response.user,
                  };
                  resolve(authenticatedUserResponse);
                  logInEvent(authenticatedUserResponse.user, authenticatedUserResponse.impersonatedBy);
                } catch (error) {
                  reject(error);
                }
              },
              onError: (error) => reject(error),
            },
          );
        });
      },
      async signIn(data: PostAuthenticationSignInRequestBodyData): Promise<AuthenticatedUser> {
        return new Promise((resolve, reject) => {
          signIn(
            { bodyData: data },
            {
              onSuccess: async (response) => {
                try {
                  await handleSignInSuccess(response);
                  const authenticatedUserResponse: AuthenticatedUser = {
                    user: response.user,
                  };
                  resolve(authenticatedUserResponse);
                  logInEvent(authenticatedUserResponse.user, authenticatedUserResponse.impersonatedBy);
                } catch (error) {
                  reject(error);
                }
              },
              onError: (error) => reject(error),
            },
          );
        });
      },
      async signInWithToken(data: PostAuthenticationOtpRequestBodyData): Promise<AuthenticatedUser> {
        return new Promise((resolve, reject) => {
          signInWithToken(
            { bodyData: data },
            {
              onSuccess: async (response) => {
                try {
                  await handleSignInSuccess(response);
                  const authenticatedUserResponse: AuthenticatedUser = {
                    impersonatedBy: response.impersonated_by ?? undefined,
                    user: response.user,
                  };
                  resolve(authenticatedUserResponse);
                  logInEvent(authenticatedUserResponse.user, authenticatedUserResponse.impersonatedBy);
                } catch (error) {
                  reject(error);
                }
              },
              onError: (error) => reject(error),
            },
          );
        });
      },
      async resetPassword(data: PostUserResetPasswordRequestBodyData): Promise<AuthenticatedUser> {
        return new Promise((resolve, reject) => {
          resetPassword(
            { bodyData: data },
            {
              onSuccess: async (response) => {
                try {
                  await handleSignInSuccess(response);
                  const authenticatedUserResponse: AuthenticatedUser = {
                    user: response.user,
                  };
                  resolve(authenticatedUserResponse);
                  resetAuth();
                  navigate(paths.login);
                } catch (error) {
                  reject(error);
                }
              },
              onError: (error) => reject(error),
            },
          );
        });
      },
      async signOut(): Promise<void> {
        try {
          /**
           * TODO: MVP-1834
           *
           * Originally, we didn't need to explicitly navigate() here, because
           * `RequireAuth` would auto-redirect to `/login` when accessToken is
           * removed. However, `RequireAuth` also sets `{ state: { loginDeeplink:
           * '...' } }` while navigating to `/login`, which is used to determine
           * whether to show a regular login page or an access denied (login
           * required) page. Navigating early here prevents the access denied page
           * from showing up when clicking logout. This is a temporary solution
           * that isn't airtight, since it relies on the React render cycle and
           * batching of state updates.
           */
          navigate(paths.login);
          logOutEvent(authenticatedUser.user);
          resetAuth();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          Logger.error(`Failed signing out: ${e.message}`);
        }
      },

      async signOutAndInvalidateSession() {
        const authToken = tokenStorage.getItem(TokenStorageKeys.AUTH_TOKEN);
        const refreshToken = tokenStorage.getItem(TokenStorageKeys.REFRESH_TOKEN);

        if (authToken && refreshToken) {
          await tokenSignOut({ bodyData: { access: authToken, refresh: refreshToken } });
        }

        await auth.signOut();
      },
    }),
    // dependency check disabled to not rely on the mutations from react-query to be memoized
    [tokenStorage], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const authenticatedUserRef = useRef(authenticatedUser);
  useEffect(() => {
    authenticatedUserRef.current = authenticatedUser;
  }, [authenticatedUser]);

  const fetchAndSetAuthenticatedUser = useCallback(async () => {
    try {
      const user = await getRequest<GetUserMeSuccessDto>(GET_USER_ME_PATH);
      const impersonatedBy = sessionStorage.getItem(IMPERSONATED_BY_STORAGE_KEY) ?? undefined;
      setAuthenticatedUser({ impersonatedBy, user });
      Sentry.setUser(user);
    } catch {
      await auth.signOut();
    }
  }, [auth]);

  const hasFetchedOnBootRef = useRef(false);

  useEffect(() => {
    const storageAccessToken = tokenStorage.getItem(TokenStorageKeys.AUTH_TOKEN);
    if (storageAccessToken && !hasFetchedOnBootRef.current) {
      hasFetchedOnBootRef.current = true;
      setAccessToken(storageAccessToken);
      fetchAndSetAuthenticatedUser();
    }

    setIsTokenLoadedFromStorage(true);

    return tokenRefresher.subscribeTokenChanges(CustomLandlerAuthenticationProvider.name, {
      onRefresh: async (newToken) => {
        setAccessToken(newToken);
      },
      onInvalidate: async () => {
        await auth.signOut().then(() => {
          navigate(paths.login, { state: { loginDeeplink: `${location.pathname}${location.search}` } });
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, fetchAndSetAuthenticatedUser, tokenStorage]);

  const updateStateOnWindowFocus = () => {
    const storageAccessToken = tokenStorage.getItem(TokenStorageKeys.AUTH_TOKEN);

    // the token cookie is gone, meaning the user signed out (possibly in another tab) - reset state
    if (!storageAccessToken) {
      resetAuth();
      return;
    }

    setAccessToken(storageAccessToken);
    setIsTokenLoadedFromStorage(true);

    // Only fetch if we don't already have a user - covers "signed in in another tab" without
    // firing a `user/me` request on every single window focus.
    if (!authenticatedUserRef.current.user) {
      fetchAndSetAuthenticatedUser();
    }
  };

  // eslint-disable-next-line prefer-arrow-callback
  useEffect(function applyEventListenerToUpdateStateOnWindowFocus() {
    window.addEventListener('focus', updateStateOnWindowFocus);

    return () => {
      window.removeEventListener('focus', updateStateOnWindowFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authContextValue = useMemo(
    () => ({
      ...auth,
      accessToken,
      isLoading: isSigningUp || isSigningIn || isSigningInWithToken || isResettingPassword,
      isSignedIn: !!accessToken,
      isBooted: isTokenLoadedFromStorage && (!accessToken || !!authenticatedUser.user),
      authenticatedUser,
      setUser,
    }),

    [
      auth,
      accessToken,
      isSigningUp,
      isSigningIn,
      isSigningInWithToken,
      isResettingPassword,
      isTokenLoadedFromStorage,
      authenticatedUser,
      setUser,
    ],
  );

  return <AuthenticationProvider value={authContextValue}>{children}</AuthenticationProvider>;
};
